import { defineStore } from 'pinia'
import { require } from '@logistiek-zonder-papier/lzp-library'
import { ref } from 'vue'
import type { Journey } from '@/types/journey'
import { type BulkEditJourneyRequest, type EditJourneyForm } from '@/model/edit-journey.model.ts'
import {
  type CreateJourneyForm,
  type CreateJourneyProduct,
  type CreateJourneyRequest,
} from '@/model/create-journey.model.ts'
import type { JourneyTemplate } from '@/types/journey-template.ts'
import type { Organization } from '@/types/organization'
import type { Driver } from '@/types/driver'
import { axiosClient } from '@/utils/axios-client'

export const useJourneysStore = defineStore('JourneysStore', () => {
  const allJourneys = ref<Journey[] | null>(null)

  function setJourneys(journeys: Journey[]) {
    allJourneys.value = journeys
  }

  function addJourneys(journey: Journey) {
    allJourneys.value?.push(journey)
  }

  async function submitNewJourneyForm(form: CreateJourneyForm, journeyData: JourneyTemplate) {
    const carrierOrganisation: Organization = require(form.carrierOrganisation, 'Carrier Organisation not set')
    const carrierDriver: Driver = require(form.carrierDriver, 'Carrier Driver not set')

    const requestData: CreateJourneyRequest = {
      wasteStreamUuid: journeyData.stroomUUID,
      products: form.products.map(
        (product): CreateJourneyProduct => ({
          uuid: require(product.uuid, 'Product UUID cannot be null in new journey'),
          weight: String(product.gewicht),
          rdCode: require(product.rdCode, 'RD Code cannot be null in a journey'),
        })
      ),
      carrierUuid: carrierOrganisation.organisatieUUID,
      remark: form.remark,
      licencePlate: carrierDriver.registration.licenseplate,
      startDate: form.startDate.toISOString(),
      endDate: form.endDate.toISOString(),
      status: require(form.status?.id, 'Status is not set'),
      deviceUuid: carrierDriver.deviceUUID,
      appId: carrierDriver.appId,
    }

    await axiosClient.request({ url: '/idf/api/journey/create', method: 'POST', data: requestData })
  }

  async function submitBulkEditJourneyForm(journeys: Journey[], editRideInputs: EditJourneyForm) {
    const requestData: BulkEditJourneyRequest[] = journeys.map(
      (journey): BulkEditJourneyRequest => {
        const products =
          editRideInputs.products == null
            ? undefined
            : editRideInputs.products.map(product => ({
                productId: product.uuid ?? null,
                quantity:
                  product.gewicht == null || String(product.gewicht) == ''
                    ? null
                    : Number(product.gewicht),
                rdCode: product.rdCode?.code ?? null,
              }))
        return {
          id: journey.ritUUID,
          reasonForEdit: editRideInputs.remark,
          status: editRideInputs.status?.id,
          departureTimestamp: editRideInputs.departureTimestamp,
          arrivalTimestamp: editRideInputs.arrivalTimestamp,
          licensePlate: editRideInputs.carrierDriver?.registration.licenseplate ?? null,
          driverRegistrationId: editRideInputs.carrierDriver?.id ?? null,
          transportingOrganizationId:
            editRideInputs.carrierDriver?.vehicle?.organization?.organisatieUUID ?? null,
          templateUuid: editRideInputs.template?.stroomUUID ?? null,
          userSignedTemplate: editRideInputs.userSignedTemplate ?? null,
          products,
        }
      }
    )

    return axiosClient.request<BulkEditJourneyRequest>({
      url: '/api/v2/journey/bulk',
      method: 'PUT',
      data: requestData,
    })
  }

  return {
    allJourneys,
    setJourneys,
    addJourneys,
    submitNewJourneyForm,
    submitBulkEditJourneyForm,
  }
})
